import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { apiFetch } from "@/lib/api-fetch";
import {
  FileCheck,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Thermometer,
  RotateCcw,
  Calendar,
  ShieldCheck,
  Flame,
} from "lucide-react";

type Windrow = {
  id: number;
  row_id: string;
  name: string;
  status: string;
  start_date: string;
  cubic_yards: number | null;
  recipe: string | null;
  last_temp: number | null;
  temp_count: number;
  turn_count: number;
};

type WindrowDetail = {
  windrow: Windrow;
  compliance: {
    consecutive_days: number;
    required_days: number;
    min_temp: number;
  };
  temperatures: Array<{ temperature: number; recorded_at: string }>;
  turns: Array<{ turn_number: number; turned_at: string }>;
};

export default function ReportsPage() {
  const { token } = useAuth();
  const [windrows, setWindrows] = useState<Windrow[]>([]);
  const [details, setDetails] = useState<Record<number, WindrowDetail>>({});
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setWindrows([]);
    setError("");
    setLoading(true);
    setDetails({});
    apiFetch("/api/windrows", {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    })
      .then((r) => { if (!r.ok) throw new Error("Unable to load reports"); return r.json(); })
      .then(async (data) => {
        if (controller.signal.aborted) return;
        const all = data.windrows || [];
        setWindrows(all);
        setLoading(false);

        // Fetch compliance details for active/complete windrows
        const toFetch = all.filter((w: Windrow) =>
          w.status === "active" || w.status === "cooking_complete"
        );
        if (toFetch.length > 0) {
          setLoadingDetails(true);
          const results: Record<number, WindrowDetail> = {};
          await Promise.all(
            toFetch.map(async (w: Windrow) => {
              try {
                const res = await apiFetch(`/api/windrows/${w.id}`, {
                  headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
                });
                if (res.ok) {
                  results[w.id] = await res.json();
                }
              } catch {}
            })
          );
          if (!controller.signal.aborted) { setDetails(results); setLoadingDetails(false); }
        }
      })
      .catch(() => { if (!controller.signal.aborted) setError("Unable to load reports. Check your connection and try again."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [token, retry]);

  const activeWindrows = windrows.filter((w) => w.status === "active" || w.status === "cooking_complete");
  const compliantCount = Object.values(details).filter(
    (d) => d.compliance.consecutive_days >= d.compliance.required_days
  ).length;
  const totalActive = activeWindrows.length;
  const complianceRate = totalActive > 0 ? Math.round((compliantCount / totalActive) * 100) : 0;
  const completeDetails = !loading && !loadingDetails && !error && Object.keys(details).length === totalActive;

  return (
    <div className="space-y-8 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-display text-3xl text-foreground">Compliance Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            EPA compliance tracking and audit-ready summaries
          </p>
        </div>
        {/* Export PDF — hidden until AI compliance report is built (Phase 10) */}
      </div>

      {error && <div role="alert" className="p-3 border border-red-300 rounded-lg text-sm">{error} <button onClick={() => setRetry(n => n + 1)} className="min-h-11 px-3 font-semibold">Retry</button></div>}
      {!loading && !loadingDetails && !error && !completeDetails && <div role="status" className="p-3 border border-border rounded-lg text-sm">Some record details are unavailable. <button onClick={() => setRetry(n => n + 1)} className="min-h-11 px-3 font-semibold">Retry</button></div>}
      {/* Overall compliance */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-4 mb-5">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
            complianceRate >= 100
              ? "bg-forest-100 dark:bg-forest-900/30"
              : "bg-amber-100 dark:bg-amber-900/30"
          }`}>
            <ShieldCheck size={28} className={complianceRate >= 100 ? "text-forest-600 dark:text-forest-400" : "text-amber-600 dark:text-amber-400"} />
          </div>
          <div>
            <h2 className="heading-display text-xl text-foreground">Overall Compliance</h2>
            <p className="text-sm text-muted-foreground">
              40 CFR Part 503 — Windrow composting requirements
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-5 bg-secondary/70 rounded-xl border border-border">
            <p className={`stat-value text-4xl ${complianceRate >= 100 ? "text-forest-600 dark:text-forest-400" : "text-amber-600 dark:text-amber-400"}`}>
              {completeDetails && totalActive > 0 ? complianceRate : "—"}<span className="text-xl text-muted-foreground ml-0.5">%</span>
            </p>
            <p className="label-caps text-muted-foreground mt-2">Compliance Rate</p>
          </div>
          <div className="text-center p-5 bg-secondary/70 rounded-xl border border-border">
            <p className="stat-value text-4xl text-foreground">
              {completeDetails ? compliantCount : "—"}<span className="text-xl text-muted-foreground ml-0.5">/{totalActive}</span>
            </p>
            <p className="label-caps text-muted-foreground mt-2">Windrows Compliant</p>
          </div>
          <div className="text-center p-5 bg-secondary/70 rounded-xl border border-border">
            <p className="stat-value text-4xl text-foreground">131<span className="text-xl text-muted-foreground ml-0.5">°F</span></p>
            <p className="label-caps text-muted-foreground mt-2">Min Threshold</p>
          </div>
        </div>
      </div>

      {/* Requirements reference */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="heading-display text-lg text-foreground mb-4">EPA Compliance Requirements</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3.5 p-4 bg-secondary/70 rounded-xl border border-border">
            <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <Flame size={17} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">Minimum Temperature</p>
              <p className="text-xs text-muted-foreground mt-0.5">131°F (55°C) throughout the windrow</p>
            </div>
          </div>
          <div className="flex items-start gap-3.5 p-4 bg-secondary/70 rounded-xl border border-border">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <Calendar size={17} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">Duration</p>
              <p className="text-xs text-muted-foreground mt-0.5">15 consecutive days above threshold</p>
            </div>
          </div>
          <div className="flex items-start gap-3.5 p-4 bg-secondary/70 rounded-xl border border-border">
            <div className="w-9 h-9 rounded-lg bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center shrink-0 mt-0.5">
              <RotateCcw size={17} className="text-forest-600 dark:text-forest-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">Turning</p>
              <p className="text-xs text-muted-foreground mt-0.5">Minimum 5 turns during 15-day period</p>
            </div>
          </div>
        </div>
      </div>

      {/* Per-windrow compliance */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-6 border-b border-border">
          <h3 className="heading-display text-lg text-foreground">Per-Windrow Compliance Status</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted rounded mx-4" />
              ))}
            </div>
          </div>
        ) : activeWindrows.length === 0 ? (
          <div className="p-16 text-center">
            <FileCheck size={36} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No active windrows to report on</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {activeWindrows.map((windrow) => {
              const detail = details[windrow.id];
              const compliance = detail?.compliance;
              const isCompliant = compliance && compliance.consecutive_days >= compliance.required_days;
              const consecutiveDays = compliance?.consecutive_days || 0;
              const progress = compliance
                ? Math.min(100, (consecutiveDays / compliance.required_days) * 100)
                : 0;
              const turnsInPeriod = detail?.turns?.length || windrow.turn_count || 0;

              return (
                <Link
                  key={windrow.id}
                  to={`/app/windrows/${windrow.id}`}
                  className="group flex items-center justify-between p-5 hover:bg-secondary/30 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isCompliant
                        ? "bg-forest-100 dark:bg-forest-900/30"
                        : "bg-amber-100 dark:bg-amber-900/30"
                    }`}>
                      {isCompliant ? (
                        <CheckCircle2 size={18} className="text-forest-600 dark:text-forest-400" />
                      ) : (
                        <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5">
                        <p className="text-sm font-bold text-foreground">{windrow.name}</p>
                        <span className="text-xs text-muted-foreground/60 font-mono tracking-wide">{windrow.row_id}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1.5">
                        <span className="text-xs text-muted-foreground flex items-center gap-1 tabular-nums">
                          <Calendar size={10} />
                          {compliance ? `${consecutiveDays}/15 days` : loadingDetails ? "Loading…" : "Details unavailable"}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1 tabular-nums">
                          <Thermometer size={10} />
                          {windrow.temp_count} readings
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1 tabular-nums">
                          <RotateCcw size={10} />
                          {turnsInPeriod} turns
                        </span>
                      </div>
                      {/* Mini progress bar */}
                      {!loadingDetails && (
                        <div className="mt-2.5 h-1.5 bg-secondary rounded-full overflow-hidden max-w-xs">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              isCompliant ? "bg-forest-500" : "bg-amber-500"
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                      isCompliant
                        ? "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                    }`}>
                      {!compliance ? (loadingDetails ? "Loading…" : "Unavailable") : isCompliant ? "Compliant" : "In Progress"}
                    </span>
                    <ChevronRight size={16} className="text-muted-foreground/40 group-hover:text-foreground transition-colors" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>


    </div>
  );
}
